import React from "react";
import { Card, Button, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import "./OrderSummary.scss";

interface OrderSummaryProps {
  totalItems: number;
  subtotal: number;
  shippingCost: number;
  totalCost: number;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
  showButton?: boolean;
  showShippingCost: boolean;
  originalShippingCost?: number; // Costo original antes del descuento
  showNoReturnsPolicy?: boolean; // Controlar si mostrar la política de no devoluciones
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  totalItems,
  subtotal,
  shippingCost,
  totalCost,
  buttonText = "Proceder al pago",
  onButtonClick = () => {},
  buttonDisabled = false,
  showButton = true,
  showShippingCost,
  originalShippingCost = 0,
  showNoReturnsPolicy = false,
}) => {
  // Calcular si aplica envío gratis
  const isFreeShipping = totalItems >= 3 && shippingCost === 0 && originalShippingCost > 0;
  return (
    <>
      <Card className="order-summary-card">
        <Card.Body>
          {/* Fila para el subtotal */}
          <div className="summary-row">
            <span className="summary-label">Subtotal</span>
            <span className="summary-subtotal">
              $
              {subtotal.toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          
          {/* Fila separada para el número de productos */}
          <div className="summary-row items-count">
            <span className="summary-label">({totalItems} productos)</span>
          </div>

          {showShippingCost && (
            <div className="summary-row">
              <span className="summary-label">Costo de envío</span>
              <div className="d-flex flex-column align-items-end">
                <span 
                  className="summary-value"
                  style={{ 
                    color: isFreeShipping ? "#28a745" : "inherit",
                    fontWeight: isFreeShipping ? "bold" : "inherit"
                  }}
                >
                  {isFreeShipping ? "GRATIS" : `$${shippingCost.toLocaleString("es-MX", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </span>
                {isFreeShipping && (
                  <span 
                    className="text-success small"
                    style={{ fontSize: "0.8em", marginTop: "2px" }}
                  >
                    🎉 ¡Envío gratis por 3+ productos!
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="summary-row total-row">
            <span className="summary-label">Total</span>
            <span className="summary-value total-value">
              $
              {totalCost.toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </Card.Body>
      </Card>
      {showButton && (
        <div className="button-container">
          {/* Notificación de política de no devoluciones */}
          {showNoReturnsPolicy && (
            <Alert 
              variant="warning" 
              className="no-returns-alert mb-3"
              style={{
                fontSize: "0.85rem",
                padding: "0.75rem",
                border: "1px solid #ffeaa7",
                backgroundColor: "#fff3cd",
                color: "#856404",
                borderRadius: "8px",
                marginBottom: "1rem"
              }}
            >
              <div className="d-flex align-items-start">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  className="me-2 mt-1"
                  style={{ fontSize: "0.9rem", color: "#f39c12" }}
                />
                <div>
                  <strong>Política de no devoluciones:</strong> Una vez confirmado tu pedido, no se aceptan devoluciones ni cambios. 
                  Por favor, verifica cuidadosamente tu selección antes de proceder al pago.
                </div>
              </div>
            </Alert>
          )}
          
          <Button
            variant="primary"
            className="checkout-btn"
            disabled={buttonDisabled}
            onClick={onButtonClick}
          >
            {buttonText}
          </Button>
        </div>
      )}
    </>
  );
};

export default OrderSummary;
